// Espace locataire et garant : mes locations, mon bail et mes documents, états des lieux, livret, clés (F-042).
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { PublicShell } from "@core/layout";
import { extensions } from "@core/modules";
import { useSession } from "@core/session";
import { Button, Card, Empty, ErrorBox, Input, KeyValues, Loading, Notice, StatusChip, Textarea, fieldError } from "@core/ui";
import { DocLink, InspectionStatus, LeaseStatus, SignerStatus, conditionLabel, useAction, type Doc, type Inspection, type SignatureRequest } from "./shared";

type MyLease = { id: string; status: string; status_label: string; regime_label: string; start_on: string; end_on: string | null; property_label: string; property_address: string; role: string; role_label: string; landlord_space: string };

export function MyLeasesPage() {
  const q = useGet<{ items: MyLease[] }>("/api/v1/me/leases");
  return (
    <PublicShell wide>
      <p>
        <Link to="/moi">Mon espace personnel</Link>
      </p>
      <h1>Ma location</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? (
        <Empty title="Aucune location rattachée à votre compte">
          Quand votre bail est signé, le bailleur vous envoie un lien pour rattacher votre compte. Ouvrez-le après vous être connecté avec l’adresse qui a reçu l’invitation.
        </Empty>
      ) : null}
      <div className="stack">
        {(q.data?.items ?? []).map((l) => (
          <Card key={l.id} title={<Link to={`/moi/locations/${l.id}`}>{l.property_label}</Link>}>
            <KeyValues
              items={[
                ["Statut", <LeaseStatus status={l.status} />],
                ["Adresse", l.property_address || "—"],
                ["Votre qualité", l.role_label],
                ["Bail", `${l.regime_label}, depuis le ${date(l.start_on)}`],
                ["Bailleur", l.landlord_space],
              ]}
            />
          </Card>
        ))}
      </div>
    </PublicShell>
  );
}

type TenantLease = {
  lease: { id: string; status: string; status_label: string; regime_label: string; start_on: string; end_on: string | null; duration_months: number | null; rent_cents: number; charges_cents: number; charges_mode: string; deposit_cents: number; payment_day: number; activated_at: string | null };
  property: { label: string; address: string };
  role: string;
  role_label: string;
  parties: { role_label: string; full_name: string }[];
  documents: { label: string; document: Doc }[];
  signature: SignatureRequest | null;
  inspections: { id: string; kind: string; kind_label: string; version: number; status: Inspection["status"]; status_label: string; performed_at: string | null; awaiting_my_validation: boolean; via_provider: boolean; pdf: Doc }[];
  handbook: { sections: { theme: string; title: string; body: string }[]; version: number; updated_at: string; themes: { code: string; label: string }[] } | null;
  handover: { keys: { label: string; count: number }[]; handed_over_on: string | null; attestation: Doc; landlord_confirmed_at: string | null; tenant_confirmed_at: string | null; can_confirm: boolean } | null;
  deposit: { expected_cents: number; received_cents: number; status: string } | null;
};

export function MyLeasePage() {
  const { leaseId = "" } = useParams();
  const invalidate = useInvalidate();
  const q = useGet<TenantLease>(`/api/v1/me/leases/${leaseId}`);
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) {
    return (
      <PublicShell>
        <ErrorBox error={q.error} />
        <Link to="/moi/locations">Mes locations</Link>
      </PublicShell>
    );
  }
  const t = q.data;
  const l = t.lease;
  const sections = extensions("tenantSections", { data: t, role: t.role });
  const mySigner = t.signature?.signers.find((s) => s.mine);
  return (
    <PublicShell wide>
      <p>
        <Link to="/moi/locations">Mes locations</Link>
      </p>
      <h1>{t.property.label}</h1>
      <p>
        <LeaseStatus status={l.status} /> <span className="muted">{t.property.address}</span>
      </p>
      <nav aria-label="Sections" className="entree-actions">
        <a href="#bail">Mon bail et mes documents</a>
        {t.role === "locataire" ? (
          <>
            <a href="#etats-des-lieux">États des lieux</a>
            <a href="#livret">Livret du logement</a>
            <a href="#cles">Remise des clés</a>
          </>
        ) : null}
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`}>
            {s.label}
          </a>
        ))}
      </nav>
      <div className="stack">
        <section id="bail" className="entree-tenant-section">
          <Card title="Mon bail et mes documents">
            <KeyValues
              items={[
                ["Votre qualité", t.role_label],
                ["Régime", l.regime_label],
                ["Prise d’effet", date(l.start_on)],
                ["Durée", l.duration_months ? `${l.duration_months} mois${l.end_on ? `, jusqu’au ${date(l.end_on)}` : ""}` : "—"],
                ["Loyer hors charges", <span className="entree-money">{money(l.rent_cents)}</span>],
                [l.charges_mode === "forfait" ? "Charges (forfait)" : "Provision sur charges", <span className="entree-money">{money(l.charges_cents)}</span>],
                ["Paiement", `Le ${l.payment_day} de chaque mois`],
                ["Dépôt de garantie", <span className="entree-money">{money(l.deposit_cents)}</span>],
                ["Parties", t.parties.map((p) => `${p.full_name} (${p.role_label.toLowerCase()})`).join(", ")],
              ]}
            />
            {t.deposit ? (
              <p className="small">
                Dépôt de garantie reçu par le bailleur : {money(t.deposit.received_cents)} sur {money(t.deposit.expected_cents)}.
              </p>
            ) : null}
            {t.signature ? (
              <Notice tone="attention" title="Signature en cours">
                {mySigner ? (
                  <>
                    Votre signature : <SignerStatus s={mySigner} />
                    {mySigner.signing_url ? (
                      <span className="block">
                        <a href={mySigner.signing_url} target="_blank" rel="noreferrer">
                          Ouvrir mon lien de signature
                        </a>
                        {t.signature.mode === "simulateur" ? " (simulation : aucun document réel n’est signé)" : ""}
                      </span>
                    ) : null}
                  </>
                ) : (
                  "Les parties signent dans l’ordre prévu."
                )}{" "}
                Échéance : {dateTime(t.signature.expires_at)}.
              </Notice>
            ) : null}
            <h3>Documents</h3>
            {t.documents.length === 0 ? <p className="muted">Aucun document pour le moment.</p> : null}
            <ul className="entree-list">
              {t.documents.map((d, i) => (
                <li key={i}>
                  <DocLink doc={d.document} label={d.label} />
                </li>
              ))}
            </ul>
          </Card>
        </section>
        {t.role === "locataire" ? (
          <>
            <section id="etats-des-lieux" className="entree-tenant-section">
              <Card title="États des lieux">
                {t.inspections.length === 0 ? <p className="muted">Aucun état des lieux à consulter pour le moment.</p> : null}
                <ul className="entree-list">
                  {t.inspections.map((i) => (
                    <li key={i.id}>
                      <div className="entree-line">
                        <Link to={`/moi/locations/${leaseId}/etats-des-lieux/${i.id}`}>
                          {i.kind_label} · version {i.version}
                        </Link>
                        <InspectionStatus i={i} />
                      </div>
                      {i.awaiting_my_validation ? <StatusChip status="a_confirmer" label="Votre validation est attendue" /> : null}
                      {i.pdf ? <DocLink doc={i.pdf} label="PDF validé" /> : null}
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
            <section id="livret" className="entree-tenant-section">
              <Card title="Livret du logement" actions={t.handbook ? <a href={`/api/v1/me/leases/${leaseId}/handbook/pdf`} target="_blank" rel="noreferrer">PDF</a> : null}>
                {!t.handbook ? <p className="muted">Le bailleur n’a pas encore rédigé le livret.</p> : null}
                {t.handbook ? (
                  <div className="stack">
                    <p className="small muted">
                      Version {t.handbook.version}, mise à jour le {dateTime(t.handbook.updated_at)}. Ces informations pratiques ne modifient pas le bail.
                    </p>
                    {t.handbook.themes.map((th) => {
                      const list = t.handbook!.sections.filter((s) => s.theme === th.code && s.body.trim());
                      if (!list.length) return null;
                      return (
                        <div key={th.code}>
                          <h3>{th.label}</h3>
                          {list.map((s, i) => (
                            <div key={i}>
                              {s.title ? <strong>{s.title}</strong> : null}
                              <p style={{ whiteSpace: "pre-line" }}>{s.body}</p>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </Card>
            </section>
            <section id="cles" className="entree-tenant-section">
              <Card title="Remise des clés">
                {!t.handover || !t.handover.attestation ? <p className="muted">La remise des clés n’a pas encore été enregistrée par le bailleur.</p> : null}
                {t.handover?.attestation ? (
                  <div className="stack">
                    <p>Clés remises le {date(t.handover.handed_over_on)} :</p>
                    <ul className="list-plain">
                      {t.handover.keys.map((k) => (
                        <li key={k.label}>
                          {k.label} : {k.count}
                        </li>
                      ))}
                    </ul>
                    <DocLink doc={t.handover.attestation} label="Attestation de remise des clés" />
                    <ErrorBox error={error} />
                    {t.handover.tenant_confirmed_at ? (
                      <StatusChip status="conforme" label={`Réception confirmée le ${dateTime(t.handover.tenant_confirmed_at)}`} />
                    ) : t.handover.can_confirm ? (
                      <Button busy={busy === "keys"} onClick={() => run("keys", async () => { await post(`/api/v1/me/leases/${leaseId}/handover/confirm`); invalidate(`/api/v1/me/leases/${leaseId}`); }, "Réception des clés confirmée.")}>
                        Je confirme avoir reçu ces clés
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </Card>
            </section>
          </>
        ) : null}
        {sections.map((s) => (
          <section key={s.id} id={s.id} className="entree-tenant-section">
            <s.Component leaseId={leaseId} />
          </section>
        ))}
      </div>
    </PublicShell>
  );
}

export function TenantInspectionPage() {
  const { leaseId = "", inspectionId = "" } = useParams();
  const { user } = useSession();
  const invalidate = useInvalidate();
  const q = useGet<Inspection & { awaiting_my_validation: boolean }>(`/api/v1/me/leases/${leaseId}/inspections/${inspectionId}`);
  const [name, setName] = useState(user?.full_name ?? "");
  const [reserves, setReserves] = useState("");
  const { busy, error, run } = useAction();
  if (q.isLoading) return <Loading />;
  if (!q.data) {
    return (
      <PublicShell>
        <ErrorBox error={q.error} />
        <Link to={`/moi/locations/${leaseId}`}>Revenir à ma location</Link>
      </PublicShell>
    );
  }
  const s = q.data;
  return (
    <PublicShell wide>
      <p>
        <Link to={`/moi/locations/${leaseId}`}>Revenir à ma location</Link>
      </p>
      <h1>
        {s.kind_label} · version {s.version}
      </h1>
      <p>
        <InspectionStatus i={s} /> {s.performed_at ? <span className="muted">Réalisé le {dateTime(s.performed_at)}</span> : null}
      </p>
      <div className="stack">
        {s.content.rooms.map((r) => (
          <Card key={r.key} title={r.name}>
            <ul className="entree-list">
              {r.items.map((i) => {
                const ps = s.photos.filter((p) => p.room_key === r.key && p.item_key === i.key);
                return (
                  <li key={i.key}>
                    <div className="entree-line">
                      <strong>{i.name}</strong>
                      <span>{conditionLabel(i.condition)}</span>
                    </div>
                    {i.comment ? <span className="small">{i.comment}</span> : null}
                    {ps.length ? (
                      <div className="entree-photos">
                        {ps.map((p) => (
                          <a key={p.id} className="entree-photo" href={p.url} target="_blank" rel="noreferrer">
                            <img src={p.url} alt={`Photo : ${r.name}, ${i.name}`} loading="lazy" />
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {r.comment ? <p className="small">{r.comment}</p> : null}
          </Card>
        ))}
        {s.content.meters.length ? (
          <Card title="Compteurs">
            <ul className="list-plain">
              {s.content.meters.map((m) => (
                <li key={m.key}>
                  {m.label} {m.number ? `n° ${m.number}` : ""} : <strong>{m.reading || "non relevé"}</strong> {m.unit}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
        {s.content.keys.length ? (
          <Card title="Clés et badges">
            <ul className="list-plain">
              {s.content.keys.map((k) => (
                <li key={k.key}>
                  {k.label} : {k.count}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
        {s.reserves ? <Notice title="Réserves">{s.reserves}</Notice> : null}
        {s.pdf ? <DocLink doc={s.pdf} label="État des lieux validé (PDF)" /> : null}
        {s.awaiting_my_validation ? (
          <Card title="Ma validation">
            <p className="small">
              Le bailleur a validé ce contenu le {dateTime(s.landlord_signed_at)}. En validant à votre tour, vous confirmez qu’il décrit l’état du logement ; vous pouvez ajouter des réserves. La validation est horodatée et liée à votre compte.
            </p>
            <ErrorBox error={error} />
            <Input label="Votre nom" value={name} error={fieldError(error, "name")} onChange={(e) => setName(e.target.value)} />
            <Textarea label="Réserves (facultatif)" value={reserves} error={fieldError(error, "reserves")} onChange={(e) => setReserves(e.target.value)} />
            <Button busy={busy === "sign"} onClick={() => run("sign", async () => { await post(`/api/v1/me/leases/${leaseId}/inspections/${inspectionId}/sign`, { name, reserves }); invalidate(`/api/v1/me/leases/${leaseId}`); }, "Validation enregistrée.")}>
              Je valide cet état des lieux
            </Button>
          </Card>
        ) : null}
      </div>
    </PublicShell>
  );
}

export function ClaimLeasePage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const { user } = useSession();
  const q = useGet<{ email_hint: string; property_label: string; role_label: string }>(`/api/v1/lease-invitations/${encodeURIComponent(token)}`);
  const { busy, error, run } = useAction();
  return (
    <PublicShell>
      <h1>Rattacher ma location</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? error} />
      {q.data ? (
        <div className="stack">
          <p>
            Vous êtes invité(e) comme {q.data.role_label.toLowerCase()} de la location « {q.data.property_label} ». Invitation adressée à {q.data.email_hint}.
          </p>
          <p className="small muted">Connecté(e) en tant que {user?.email}. Si ce n’est pas l’adresse invitée, déconnectez-vous et reconnectez-vous avec la bonne adresse.</p>
          <Button
            busy={busy === "claim"}
            onClick={async () => {
              let id = "";
              if (await run("claim", async () => { const r = await post<{ lease_id: string }>(`/api/v1/lease-invitations/${encodeURIComponent(token)}/accept`); id = r.lease_id; }, "Location rattachée à votre compte.")) nav(`/moi/locations/${id}`);
            }}
          >
            Rattacher à mon compte
          </Button>
        </div>
      ) : null}
    </PublicShell>
  );
}

/** Page d'arrivée des liens de signature du simulateur (aucune signature réelle). */
export function SimulatorSignaturePage() {
  return (
    <PublicShell>
      <h1>Lien de signature simulé</h1>
      <Notice tone="simulateur" title="Simulateur Youtrust">
        Ce lien provient du simulateur de signature de Bailivo : aucun document réel n’est signé ici. En développement, le bailleur simule la signature depuis l’onglet « Signature » du bail.
      </Notice>
      <p>
        <Link to="/moi/locations">Mes locations</Link>
      </p>
    </PublicShell>
  );
}
